import type { Registry as Services } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import type { Occupancy } from 'lumen/models';

// For the update timestamp we want to show a fixed translation 'Updated today' if it's today instead of day.js '... hours ago'.
// Otherwise we show the relative time e.g. 'Updated 2 months ago'.
export function getOtbUpdateString(
  timestamp: Dayjs,
  hotelAndMarketService: Services['hotel-and-market'],
  intl: Services['intl'],
): string {
  if (timestamp.isSame(hotelAndMarketService.getLocalTime(), 'day')) {
    return `${intl.t(tKey('general.updated_today'))}`;
  }

  return `${intl.t(tKey('general.update'))} ${timestamp.from(
    hotelAndMarketService.getLocalTime().startOf('day'),
  )}`;
}

// Util function for the OTB out-of-sync flags and messages that are used in Rate Insight and Market Insight.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function otbOutOfSync(
  occupancies: Occupancy[],
  hotelAndMarketService: Services['hotel-and-market'],
  intl: Services['intl'],
  access: Services['access'],
) {
  const localTime = hotelAndMarketService.getLocalTime();

  // Indicate if My OTB and Market OTB are out of sync.
  const isOtbOutOfSync = occupancies?.some(
    (occupancy) => occupancy.isOtbOutOfSync,
  );
  const isMarketOtbOutOfSync = occupancies?.some(
    (occupancy) => occupancy.isMarketOtbOutOfSync,
  );
  if (!isOtbOutOfSync && !isMarketOtbOutOfSync) {
    return {
      isOtbOutOfSync,
      isMarketOtbOutOfSync,
    };
  }

  // The 'last updated on' date is the earliest extract date of current and future days.
  const currentAndFutureDays = occupancies
    ?.filter((occupancy) => !occupancy.day.date.isBefore(localTime, 'day'))
    .map((occupancy) => occupancy.day);

  // Only include properties that don't have missing data (message will be general.missing if it's missing).
  const currentAndFutureDaysWithOtbData = currentAndFutureDays?.filter(
    (day) => !day.occupancy?.isMissing,
  );
  const currentAndFutureDaysWithMarketOtbData = currentAndFutureDays?.filter(
    (day) => !day.occupancy?.isBenchmarkMissing,
  );

  const currentAndFutureOtbExtractDates = currentAndFutureDaysWithOtbData
    ?.map((day) => day.occupancy?.extractDate)
    .filter((extractDate) => extractDate !== undefined);
  const currentAndFutureMarketOtbExtractDates =
    currentAndFutureDaysWithMarketOtbData
      ?.map((day) => day.occupancy?.benchmarkExtractDate)
      ?.filter((extractDate) => extractDate !== undefined);

  // Fallback to now if dayjs.min() is null (same behaviour as for moment.min())
  const earliestOtbExtractDate =
    dayjs.min(currentAndFutureOtbExtractDates) ?? dayjs();
  const earliestMarketOtbExtractDate =
    dayjs.min(currentAndFutureMarketOtbExtractDates) ?? dayjs();

  const otbUpdatedString = getOtbUpdateString(
    earliestOtbExtractDate,
    hotelAndMarketService,
    intl,
  );
  const marketOtbUpdatedString = getOtbUpdateString(
    earliestMarketOtbExtractDate,
    hotelAndMarketService,
    intl,
  );

  const otbUpdateStringWithTitle = `${intl.t(tKey('rates.my_OTB'))} ${otbUpdatedString}`;
  const marketOtbUpdateStringWithTitle = `${intl.t(
    tKey('rates.tooltip.market_otb'),
  )} ${marketOtbUpdatedString}`;

  return {
    isOtbOutOfSync,
    isMarketOtbOutOfSync,
    // 'Updated ... days ago'
    otbUpdatedString,
    // 'Updated ... days ago'
    marketOtbUpdatedString,
    // 'My OTB Updated ... days ago' or,
    // if there is access to Market OTB: 'My OTB Updated ... days ago<br>Market OTB Updated ... days ago'
    otbAndMarketOtbUpdatedString: htmlSafe(
      `${otbUpdateStringWithTitle}${
        access.hasFeature('otb.market_benchmark')
          ? `<br>${marketOtbUpdateStringWithTitle}`
          : ''
      }`,
    ),
    otbLastUpdatedOnString: intl.t(tKey('otb.last_updated_on'), {
      otb: intl.t(tKey('rates.my_OTB')),
      date: earliestOtbExtractDate.format('L'),
    }),
    marketOtbLastUpdatedOnString: intl.t(tKey('otb.last_updated_on'), {
      otb: intl.t(tKey('rates.tooltip.market_otb')),
      date: earliestMarketOtbExtractDate.format('L'),
    }),
  };
}
